import { Box, Container, Stack, Text, useMounted } from '@qcx/ui';
import { Breadcrumb, BreadcrumbLink } from '../../components/Breadcrumb';
import { ContestCard } from './components/ContestCard';
import { EditorialsService } from '@atreus/data';
import { FollowButton } from './components/FollowButton';
import { Page } from '../../components/page/Page';
import { PageBellowTheFold } from '../../components/page/PageBellowTheFold';
import { Pagination } from '@kratos/remix-components';
import { PostSection } from '../../components/post/PostSection';
import { PrincipalArticle } from './components/PrincipalArticle';
// eslint-disable-next-line import/no-unresolved
import { Route } from './+types/EditorialsShowPage';
import { data } from 'react-router';
import { generateCanonicalUrl } from '../../lib/generate-canonical-url.lib';
import { getFirstWords } from '../../lib/get-first-words.lib';
import { replaceAll } from '../../lib/replace-all.lib';
import { useEditorialsStore } from './editorials.store';

export const meta = ({ data }: Route.MetaArgs) => {
  if (!data) {
    return [];
  }

  const currentPage = data.meta.currentPage;
  const previousPage = currentPage - 1;
  const nextPage = currentPage + 1;
  const baseUrl = data.baseUrl;
  const relTags = [];
  const canonicalUrl = generateCanonicalUrl(data.url);
  const pageQuery = currentPage === 2 ? '' : `?page=${previousPage}`;

  relTags.push({
    tagName: 'link',
    rel: 'canonical',
    href: canonicalUrl
  });

  if (currentPage > 1) {
    relTags.push({
      tagName: 'link',
      rel: 'prev',
      href: baseUrl + `/e/${data.slug}` + pageQuery
    });
  }

  if (currentPage !== data.meta.totalPages) {
    relTags.push({
      tagName: 'link',
      rel: 'next',
      href: `${baseUrl}/e/${data.slug}?page=${nextPage}`
    });
  }

  return [
    {
      title: `Concursos ${data.editorial.name.replace(
        'Concursos ',
        ''
      )} - Notícias Atualizadas | Folha Dirigida`
    },
    {
      name: 'description',
      content: `Concursos ${data.editorial.name.replace(
        'Concursos ',
        ''
      )}: Leia todas as últimas notícias. Notícias atualizadas e disponíveis dos principais concursos públicos do Brasil, em primeira mão`
    },
    { name: 'url', content: data.url },
    {
      'script:ld+json': {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Início',
            item: data.baseUrl
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Editoria',
            item: `${data.baseUrl}/e`
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: data.editorial.name
          }
        ]
      }
    },
    ...relTags
  ];
};

export const loader = async ({ params, request }: Route.LoaderArgs) => {
  const slug = params.slug as string;
  const url = new URL(request.url);
  url.protocol = 'https';
  const page = Number(url.searchParams.get('page') ?? 1);
  const editorialService = new EditorialsService();

  const editorial = await editorialService.getAtreusEditorial({
    page,
    slug
  });

  const kindTexts: Record<string, string> = {
    Instituto: 'institute',
    Região: 'region',
    Estado: 'state',
    Editorial: 'special'
  };

  const baseContentGroup = 'fd category';
  const contentGroup = `${baseContentGroup} ${
    kindTexts[editorial.editorial.kindText as string]
  }`;

  return data({
    slug: slug,
    baseUrl: url.origin,
    url: url.toString(),
    ...editorial,
    contentGroup
  });
};

const EditorialsShowPage = ({ loaderData }: Route.ComponentProps) => {
  const {
    url,
    editorial,
    contest,
    principalArticle,
    articlesByEditorial,
    meta,
    contentGroup
  } = loaderData;

  const setUserPreferences = useEditorialsStore(s => s.setUserPreferences);

  useMounted(() => {
    setUserPreferences();

    if (window?.zaraz) {
      window.zaraz.set('content_group', contentGroup);
    }
  });

  return (
    <Page>
      <Container>
        <Breadcrumb>
          <BreadcrumbLink href={'/'}>Início</BreadcrumbLink>
          <BreadcrumbLink href={'/editorias'}>Editoria</BreadcrumbLink>
          <BreadcrumbLink href={`/e/${editorial.slug}`} isCurrentPage>
            {getFirstWords(
              editorial.name ?? replaceAll(editorial.slug, ' ', '-'),
              3
            )}
          </BreadcrumbLink>
        </Breadcrumb>
      </Container>
      <Container as="section">
        <Stack
          css={{ d: 'flex', alignItems: 'center', gap: '$7' }}
          flow={{ '@initial': 'row' }}
        >
          <Text
            as="h1"
            variant={{
              '@initial': 'displaySm',
              '@md': 'displayMd'
            }}
            weight="bold"
          >
            {editorial.name}
          </Text>
          {editorial.id && editorial.kindText === 'Instituto' && (
            <FollowButton editorialId={editorial.id} />
          )}
        </Stack>
        <Stack
          flow={{
            '@initial': 'column',
            '@lg': 'row'
          }}
          gap="$8"
        >
          <Box
            css={{
              mb: '$8',
              '@lg': {
                w: '$3of4'
              }
            }}
          >
            {contest ? <ContestCard contest={contest} /> : null}
          </Box>
        </Stack>
        {principalArticle ? (
          <PrincipalArticle principalArticle={principalArticle} />
        ) : null}
        <PageBellowTheFold>
          {articlesByEditorial.length > 0 && (
            <PostSection
              css={{
                d: 'flex',
                flexWrap: 'wrap'
              }}
              flow={{ '@xs': 'column', '@md': 'row', '@lg': 'column' }}
              postCardProps={{
                flow: {
                  '@xs': 'column',
                  '@sm': 'row'
                },
                elevated: false,
                as: 'h3',
                css: { '@md': { w: 343 }, '@lg': { w: 'auto' } }
              }}
              posts={articlesByEditorial}
              title={'Notícias sobre a editoria'}
            />
          )}
          <Box css={{ flexCenter: true, mt: '$10', mb: '$8' }}>
            <Pagination
              pagination={meta}
              permittedParams={['page', 'q']}
              url={url}
            />
          </Box>
        </PageBellowTheFold>
      </Container>
    </Page>
  );
};

export default EditorialsShowPage;
